import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';

import {Observable} from 'rxjs';
import {map, share, tap} from 'rxjs/operators';

import sumBy from 'lodash-es/sumBy';

import * as moment from 'moment/moment';

import {Destroyable} from '../../../../destroyable.component';
import {EmpVariableAttributes, PayPeriodAttributes} from '../../../../models/models.interfaces';
import {Data} from '../../../../shared/api';
import {EmployeesUtilsService} from '../../../../shared/employees/employees-utils.service';
import {Contract} from '../../../../shared/employees/employees.model';
import {LocalizationService} from '../../../../shared/localization/localization.service';
import {EmployeeVariablesService} from '../employee-variables.service';

@Component({
  selector: 'gp-payroll-prepare-variable-elements-single-payperiod',
  templateUrl: 'variable-elements-single-payperiod.component.html',
  styleUrls: ['./variable-elements-single-payperiod.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VariableElementsSinglePayperiodComponent extends Destroyable implements OnChanges {

  constructor(private ref: ChangeDetectorRef,
              private employeeVariablesService: EmployeeVariablesService,
              private localizationService: LocalizationService,
              private employeesUtilsService: EmployeesUtilsService
  ) {
    super();

    this.triggerDateChange$ = this.localizationService.changeDateFormatEvent.pipe(
      map(() => Math.random()),
      share()
    );

    this.employeesUtilsService.isEmployeeFormEmbedded$.pipe(
      tap(isEmbedded => {
        this.isEmployeeFormEmbedded = isEmbedded;
      })
    ).subscribe();
  }

  @ViewChild('payPeriodList') payPeriodList: ElementRef;

  @Output()
  public onEditElement: EventEmitter<Data<EmpVariableAttributes>> = new EventEmitter();

  @Output()
  public onCreateElement: EventEmitter<Data<EmpVariableAttributes>> = new EventEmitter();

  @Input()
  public editedPayPeriod: Data<PayPeriodAttributes>;

  @Input()
  public payPeriodsList: Data<PayPeriodAttributes>[];

  @Input()
  public variableElements: { [name: string]: Data<EmpVariableAttributes>[] };

  @Input()
  public readOnly = false;

  @Input()
  public contract: Contract;

  @Input()
  public loading = false;

  @Input()
  public canNavigateBack: boolean;

  @Input()
  public canNavigateForward: boolean;

  @Input()
  public variableTypes: { [key: string]: string };

  @Input()
  public isVariablesV2Enabled: boolean;

  @Output()
  public variableElementDeletedNew: EventEmitter<{
    item: Data<EmpVariableAttributes>,
    isNested: boolean,
    type: string
  }> = new EventEmitter();

  @Output()
  public variableElementDeleted: EventEmitter<{
    item: Data<EmpVariableAttributes>,
    isNested: boolean,
    type: string
  }> = new EventEmitter();

  @Output()
  public isDirty: EventEmitter<Data<EmpVariableAttributes>> = new EventEmitter();

  @Output()
  public onPrevious: EventEmitter<void> = new EventEmitter();

  @Output()
  public onNext: EventEmitter<void> = new EventEmitter();

  @Output()
  public onNavigate: EventEmitter<number> = new EventEmitter();

  public expanded: { [key: string]: boolean } = {};

  public filteredVariables: { [name: string]: Data<EmpVariableAttributes>[] } = {};
  public recurrentVariables: { [name: string]: Data<EmpVariableAttributes>[] };
  public nonRecurrentVariables: { [name: string]: Data<EmpVariableAttributes>[] };

  public objectKeys = Object.keys;

  public localLoading = false;

  public triggerDateChange$: Observable<number>;

  public isEmployeeFormEmbedded = false;

  private wheelEventHandler: (event: WheelEvent) => void;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.payPeriodsList) {
      if (!this.wheelEventHandler) {
        this.wheelEventHandler = this.onWheelScroll.bind(this);
        this.payPeriodList?.nativeElement?.addEventListener('wheel', this.wheelEventHandler);
      }
      this.scrollToPeriod();
    }

    if (changes.variableElements) {
      this.loadVariables();
    }

    if (changes.editedPayPeriod) {
      this.scrollToPeriod();
    }
  }

  public ngOnDestroy(): void {
    if (this.wheelEventHandler) {
      this.payPeriodList?.nativeElement?.removeEventListener('wheel', this.wheelEventHandler);
    }
    super.ngOnDestroy();
  }

  public loadVariables(): void {
    this.localLoading = true;
    this.filteredVariables = {};

    for (const type in this.variableElements) {
      this.filteredVariables[type] = [];
      const filtered = this.variableElements[type].filter(variable =>
        moment(variable.attributes.enddate).isSameOrAfter(moment(this.editedPayPeriod.attributes.begindate))
        && moment(variable.attributes.begindate).isSameOrBefore(moment(this.editedPayPeriod.attributes.enddate)));
      this.filteredVariables[type] = filtered;
    }

    const {
      recurrentVariables,
      nonRecurrentVariables
    } = this.employeeVariablesService.splitVariablesByRecurrence(this.filteredVariables, this.variableTypes);
    this.recurrentVariables = recurrentVariables;
    this.nonRecurrentVariables = nonRecurrentVariables;

    this.filteredVariables = {...this.filteredVariables};
    this.localLoading = false;
    this.ref.markForCheck();
  }

  public navigatePrevious(): void {
    this.onPrevious.emit();
  }

  public navigateNext(): void {
    this.onNext.emit();
  }

  public navigateTo(id: number): void {
    this.onNavigate.emit(id);
  }

  public createVariable(): void {
    this.onCreateElement.emit();
  }

  public editVariable(variable: Data<EmpVariableAttributes>): void {
    this.onEditElement.emit(variable);
  }

  public elementDeleted(element: Data<EmpVariableAttributes>, isNested: boolean, type: string): void {
    const _element = {item: element, isNested, type};
    if (element.isNew) {
      this.variableElementDeletedNew.emit(_element);
    } else {
      this.variableElementDeleted.emit(_element);
    }
  }

  public markAsDirty(element: Data<EmpVariableAttributes>): void {
    if (!element?.id) {
      // If !element.id, item is new and has already been marked, no need to emit
      return;
    }
    this.isDirty.emit(element);
  }

  public toggleExpand(type: string): void {
    this.expanded[type] = !this.expanded[type];
  }

  public getVariableSum(variables: Data<EmpVariableAttributes>[]): number {
    return sumBy(variables, (v: Data<EmpVariableAttributes>) => +v.attributes?.value || 0);
  }

  public scrollToPeriod(): void {
    setTimeout(() => {
      const classElement = document.getElementsByClassName('btn-period active');
      if (!classElement?.length) {
        return;
      }
      classElement[classElement.length - 1].scrollIntoView({inline: 'center', behavior: 'smooth', block: 'nearest'});
    }, 100);
  }

  public dateWarning(variableEnddate: string): boolean {
    return new Date(variableEnddate) > new Date(this.contract.attributes?.enddate || this.contract.enddate);
  }

  public tableHeader(index: number): string {
    return this.employeeVariablesService.tableHeader(index);
  }

  public isTableVisible(index: number): boolean {
    return !this.isVariablesV2Enabled || index === 1;
  }

  public onWheelScroll(event: WheelEvent): void {
    const payPeriodListDiv = this.payPeriodList.nativeElement;
    if (event.deltaY > 0) {
      payPeriodListDiv.scrollLeft += 50;
    } else {
      payPeriodListDiv.scrollLeft -= 50;
    }
    event.preventDefault();
  }

  public getContractBeginDate() {
    return this.contract.attributes?.begindate ?? this.contract.begindate;
  }

  public getContractEndDate() {
    return this.contract.attributes?.enddate ?? this.contract.enddate;
  }
}
